import { ensureValidDate, ensureValidLocale } from '@opallabs/lambda'
import { type IETFLanguageTag } from '../IETFLanguageTag'
import { timeZoneName } from './timeZoneName'
import { type IANATimezoneIdentifier } from './types'
import { utcOffsetForZoneAsString } from './utcOffsetForZone'
import { ensureValidTimeZoneIdentifier } from './validateArguments'

type Options = Readonly<{
  /**
   * Whether to include the date when localizing `date`, or only return the
   * time.
   */
  includeDate?: boolean
  /**
   * Whether the localized `date` should include context about
   * `timeZoneIdentifier`.
   *
   * * When `'always'`, the localized `date` will be followed by the abbreviated
   *   time zone name (if available) or the UTC offset.
   * * When `'contextual'`, the time zone name or UTC offset will only be added
   *   if `timeZoneIdentifier` is different than the user’s current time zone.
   * * When `'never'`, the time zone name or UTC offset will never be added.
   */
  includeZoneNameOrOffset?: 'never' | 'contextual' | 'always'
  locale?: IETFLanguageTag
}>

function zoneNameOrOffset(
  date: Date,
  timeZoneIdentifier: IANATimezoneIdentifier,
  { includeZoneNameOrOffset, locale }: Omit<Options, 'includeDate'>
): string | undefined {
  if (includeZoneNameOrOffset === 'never') return

  if (includeZoneNameOrOffset === 'contextual') {
    const userZoneIdentifier = Intl.DateTimeFormat().resolvedOptions().timeZone
    if (userZoneIdentifier === timeZoneIdentifier) return
  }

  const zoneName = timeZoneName(date, timeZoneIdentifier)
  if (zoneName.short) return zoneName.short

  return utcOffsetForZoneAsString(date, timeZoneIdentifier, {
    includeGMT: true,
    locale,
  })
}

/**
 * @todo When `includeDate` is `'contextual'` this should include month and day
 *   (and year if needed) if the original `date` and adjusted date are
 *   different days in user’s time zone. Tentative ocumentation:
 *   > * When `'always'`, the localized `date` will include the year, month,
 *   >   and day.
 *   > * When `'contextual'`, the localized `date` will include the year and
 *   >   month if the adjusted calendar date is different than `date`’s
 *   >   calendar date. The year will also be included if needed.
 *   >   if `timeZoneIdentifier` is different than the user’s current time
 *   >   zone.
 *   > * When `'never'`, the localized `date` will not include the year, month,
 *   >   or day.
 */
function format(
  date: Date,
  timeZoneIdentifier: IANATimezoneIdentifier,
  { includeDate, locale }: Omit<Options, 'includeZoneNameOrOffset'>
): string {
  const formattedTimestampOptions: Intl.DateTimeFormatOptions = {
    timeStyle: 'short',
    timeZone: timeZoneIdentifier,
  }
  if (includeDate) {
    Object.assign(formattedTimestampOptions, {
      dateStyle: 'medium',
    } as Intl.DateTimeFormatOptions)
  }
  return date.toLocaleString(locale, formattedTimestampOptions)
}

/**
 * Format `date` as a string in the `timeZoneIdentifier` time zone. This is
 * similar to `date-fns-tz`’s `formatInTimeZone`, but produces localized text
 * using `Date.prototype.toLocaleString()`.
 */
export function formattedDateInTimeZone(
  date: Date,
  timeZoneIdentifier: IANATimezoneIdentifier,
  {
    includeDate = false,
    includeZoneNameOrOffset = 'contextual',
    locale = navigator.language,
  }: Options = {}
): string {
  ensureValidDate(date)
  ensureValidLocale(locale)
  ensureValidTimeZoneIdentifier(timeZoneIdentifier)

  const formatted = format(date, timeZoneIdentifier, {
    includeDate,
    locale,
  })

  const zone = zoneNameOrOffset(date, timeZoneIdentifier, {
    includeZoneNameOrOffset,
    locale,
  })
  if (zone) return `${formatted} (${zone})`

  return formatted
}
